import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowRight, Star } from "lucide-react";
import { getIconComponent } from "./iconMap";

interface ToolsGridProps {
  tools: any[];
  onToolClick: (tool: any) => void;
}

export function ToolsGrid({ tools, onToolClick }: ToolsGridProps) {
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
      {tools.map((tool) => {
        const IconComponent = getIconComponent(tool.icon);
        const isClickable = tool.is_active && !tool.is_coming_soon;
        return (
          <Card
            key={tool.id}
            className={`group hover:shadow-xl transition-all duration-300 cursor-pointer border-2 hover:border-primary/50 bg-white ${isClickable ? 'hover:scale-105 hover:bg-gray-50' : 'opacity-60 cursor-not-allowed'}`}
            onClick={() => isClickable && onToolClick(tool)}
          >
            <CardHeader className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="p-3 rounded-xl bg-gradient-to-br from-primary/10 to-accent/20 text-primary group-hover:from-primary/20 group-hover:to-accent/30 transition-all duration-300">
                  <IconComponent className="h-7 w-7" />
                </div>
                <div className="flex items-center gap-2">
                  {tool.is_featured && <Star className="h-4 w-4 text-yellow-500 fill-current" />}
                  <Badge variant={isClickable ? 'default' : 'secondary'} className="text-xs">
                    {tool.is_coming_soon ? 'Coming Soon' : tool.is_active ? 'Available' : 'Inactive'}
                  </Badge>
                </div>
              </div>
              <div>
                <CardTitle className="text-lg group-hover:text-primary transition-colors mb-2">{tool.name}</CardTitle>
                <Badge variant="outline" className="text-xs mb-2">{tool.category}</Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <CardDescription className="text-sm leading-relaxed">{tool.description}</CardDescription>
              <div className="flex flex-wrap gap-1">
                {tool.tags.slice(0, 3).map((tag: any) => (
                  <Badge key={`${tool.id}-${tag.name}`} variant="secondary" className="text-xs">
                    {tag.name}
                  </Badge>
                ))}
              </div>
              <Button variant={isClickable ? 'default' : 'secondary'} className="w-full" disabled={!isClickable}>
                {tool.is_coming_soon ? (
                  'Coming Soon'
                ) : tool.is_active ? (
                  <div className="flex items-center gap-2">
                    Open Tool
                    <ArrowRight className="h-4 w-4" />
                  </div>
                ) : (
                  'Inactive'
                )}
              </Button>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
} 